import * as React from "react";
import { useState } from "react";
import styled from "styled-components";
import { PluginStore } from "graylog-web-plugin/plugin";
import { useQueryClient } from "@tanstack/react-query";
import { InputsActions } from "stores/inputs/InputsStore";
import { InputTypesActions } from "stores/inputs/InputTypesStore";
import { getPathnameWithoutId } from "util/URLUtils";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useLocation from "routing/useLocation";
import { Col, Row, Button } from "components/bootstrap";
import { Select } from "components/common";
import { InputForm } from "components/inputs";
import useInputTypes from "components/inputs/useInputTypes";
import { KEY_PREFIX } from "hooks/usePaginatedInputs";
import useFeature from "hooks/useFeature";
import { INPUT_SETUP_MODE_FEATURE_FLAG, InputSetupWizard } from "components/inputs/InputSetupWizard";
const StyledForm = styled.form`
  display: flex;
  gap: 0.25em;
`;
const FormGroup = styled.div`
  width: 300px;
  display: inline-block;
  margin-bottom: 0;
  vertical-align: middle;
`;
const CreateInputControl = () => {
  var _a;
  const [showConfigurationForm, setShowConfigurationForm] = useState(false);
  const [selectedInput, setSelectedInput] = useState(void 0);
  const [selectedInputDefinition, setSelectedInputDefinition] = useState(void 0);
  const [customInputConfiguration, setCustomInputConfiguration] = useState(void 0);
  const [showWizard, setShowWizard] = useState(false);
  const [createdInputId, setCreatedInputId] = useState(null);
  const [createdInputData, setCreatedInputData] = useState(null);
  const sendTelemetry = useSendTelemetry();
  const { pathname } = useLocation();
  const inputTypes = useInputTypes();
  const queryClient = useQueryClient();
  const inputSetupFeatureFlagIsEnabled = useFeature(INPUT_SETUP_MODE_FEATURE_FLAG);
  const openWizard = (inputId, inputData) => {
    setCreatedInputId(inputId);
    setCreatedInputData(inputData);
    setShowWizard(true);
  };
  const closeWizard = () => {
    setShowWizard(false);
    setCreatedInputId(null);
    setCreatedInputData(null);
  };
  const resetFields = () => {
    setSelectedInput(void 0);
    setSelectedInputDefinition(void 0);
  };
  const formatSelectOptions = () => {
    let options = [];
    if (inputTypes) {
      const inputTypesIds = Object.keys(inputTypes);
      options = inputTypesIds.map((id) => ({ value: id, label: inputTypes[id] }));
      options.sort(
        (inputTypeA, inputTypeB) => inputTypeA.label.toLowerCase().localeCompare(inputTypeB.label.toLowerCase())
      );
    } else {
      options.push({ value: "none", label: "No inputs available", disabled: true });
    }
    return options;
  };
  const onInputSelect = (selected) => {
    if (selected === "") {
      resetFields();
    }
    setSelectedInput(selected);
    sendTelemetry(TELEMETRY_EVENT_TYPE.INPUTS.INPUT_SELECTED, {
      app_pathname: getPathnameWithoutId(pathname),
      app_action_value: "input-select",
      event_details: { value: selected }
    });
    InputTypesActions.get(selected).then(
      (inputDefinition) => setSelectedInputDefinition(inputDefinition)
    );
  };
  const onCustomInputClose = () => {
    setCustomInputConfiguration(void 0);
  };
  const handleInputTypeSubmit = (event) => {
    event.preventDefault();
    const customConfiguration = PluginStore.exports("inputConfiguration").find(
      (inputConfig) => inputConfig.type === selectedInput
    );
    setCustomInputConfiguration(customConfiguration);
    setShowConfigurationForm(true);
  };
  const createInput = (data) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.INPUTS.INPUT_CREATED, {
      app_pathname: getPathnameWithoutId(pathname),
      app_action_value: "input-create"
    });
    InputsActions.create(data).then((response) => {
      queryClient.invalidateQueries({ queryKey: KEY_PREFIX });
      if (inputSetupFeatureFlagIsEnabled && (response == null ? void 0 : response.id)) {
        setTimeout(() => openWizard(response.id, data), 500);
      }
      resetFields();
    });
  };
  const createInputForWizard = () => {
    if (!createdInputId || !createdInputData) return null;
    return {
      id: createdInputId,
      title: createdInputData.title,
      type: createdInputData.type,
      attributes: createdInputData.configuration,
      global: createdInputData.global || false,
      node: createdInputData.node || null
    };
  };
  const CustomInputsConfiguration = customInputConfiguration ? customInputConfiguration.component : null;
  return /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(StyledForm, { className: "form-inline", onSubmit: handleInputTypeSubmit }, /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(
    Select,
    {
      placeholder: "Select input",
      options: formatSelectOptions(),
      onChange: onInputSelect,
      value: selectedInput
    }
  )), "\xA0", /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", type: "submit", disabled: !selectedInput }, "Launch new input")), selectedInputDefinition && (customInputConfiguration ? /* @__PURE__ */ React.createElement(CustomInputsConfiguration, { onClose: onCustomInputClose }) : showConfigurationForm && /* @__PURE__ */ React.createElement(
    InputForm,
    {
      key: "configuration-form-input",
      setShowModal: setShowConfigurationForm,
      configFields: selectedInputDefinition.requested_configuration,
      description: selectedInputDefinition.description,
      title: /* @__PURE__ */ React.createElement("span", null, "Launch new ", /* @__PURE__ */ React.createElement("em", null, (_a = inputTypes[selectedInput]) != null ? _a : ""), " input"),
      submitButtonText: "Launch Input",
      typeName: selectedInput,
      handleSubmit: createInput
    }
  )), inputSetupFeatureFlagIsEnabled && showWizard && createdInputId && /* @__PURE__ */ React.createElement(InputSetupWizard, { input: createInputForWizard(), show: showWizard, onClose: closeWizard })));
};
export default CreateInputControl;
